import { useCallback, type ReactNode } from 'react';

import { useRequestLeave } from '../useRequestLeave';
import { useRightPanel } from '../useRightPanel';

/**
 * Right-panel actions that prompt when unsaved changes would be lost.
 */
export function useGuardedRightPanel() {
  const requestLeave = useRequestLeave();
  const {
    closeRightPanel,
    hasRightPanelCloseButton,
    isRightPanelCheckDirtyOnClose,
    isRightPanelOpen,
    openRightPanel,
    rightPanelContent,
    setHasRightPanelCloseButton,
    setIsRightPanelCheckDirtyOnClose,
    setRightPanelContent,
    toggleRightPanel,
  } = useRightPanel();

  const guardedCloseRightPanel = useCallback(async (): Promise<void> => {
    const canLeave = await requestLeave();
    if (!canLeave) {
      return;
    }

    closeRightPanel();
  }, [closeRightPanel, requestLeave]);

  const guardedSetRightPanelContent = useCallback(
    async (content: ReactNode): Promise<void> => {
      const canLeave = await requestLeave();
      if (!canLeave) {
        return;
      }

      setRightPanelContent(content);
    },
    [requestLeave, setRightPanelContent],
  );

  const guardedOpenRightPanel = useCallback(
    async (closeButtonVisible = true): Promise<void> => {
      const canLeave = await requestLeave();
      if (!canLeave) {
        return;
      }

      openRightPanel(closeButtonVisible);
    },
    [openRightPanel, requestLeave],
  );

  const guardedToggleRightPanel = useCallback(async (): Promise<void> => {
    if (isRightPanelOpen) {
      await guardedCloseRightPanel();
      return;
    }

    openRightPanel();
  }, [guardedCloseRightPanel, isRightPanelOpen, openRightPanel]);

  return {
    closeRightPanel,
    guardedCloseRightPanel,
    guardedOpenRightPanel,
    guardedSetRightPanelContent,
    guardedToggleRightPanel,
    hasRightPanelCloseButton,
    isRightPanelCheckDirtyOnClose,
    isRightPanelOpen,
    openRightPanel,
    rightPanelContent,
    setHasRightPanelCloseButton,
    setIsRightPanelCheckDirtyOnClose,
    setRightPanelContent,
    toggleRightPanel,
  };
}
